import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Customer } from '../../types/customer';
import { api } from '../../services/api';
import { useToast } from '../../components/UI/ToastProvider';
import { maskPhone } from '../../services/validationService';
import { formatCpfCnpjInput, validateCpfCnpj } from '../../utils/cpfCnpjUtils';
import { Button } from '../../components/UI/Button';
import { CustomerApiCredentialsPanel } from './CustomerApiCredentialsPanel';
import { CustomerExperiencePanel } from './CustomerExperiencePanel';
import { CustomerSubscriptionPanel } from './CustomerSubscriptionPanel';
import { CustomerAgentPlatformPanel } from './CustomerAgentPlatformPanel';
import {
  TenantAccessMode,
  buildBarukFqdn,
  deriveEnterpriseCustomSlugBaseFromDomain,
  isTenantAccessMode,
  normalizeSlugInput,
  validateCustomDomain,
  validateTenantSlugDnsLabel,
} from '../../services/customerTenantValidation';

type CustomerForm = Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'> & {
  tenantAccessMode: TenantAccessMode | '';
};

const initialForm: CustomerForm = {
  name: '',
  slug: '',
  domain: '',
  tenantAccessMode: TenantAccessMode.EnterpriseCustom,
  legalName: '',
  cnpjCpf: '',
  email: '',
  phone: '',
  appTitle: '',
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '',
  secondaryColor: '',
  timezone: '',
  language: '',
  dateFormat: '',
  isActive: true,
  isOwner: false,
  enableEmailNotifications: true,
  suggestWorkflowByDefault: false,
};

type EditTab = 'dados' | 'licenciamento' | 'credenciais';

const OWNER_LICENSING_TAB_TOOLTIP =
  'Tenant owner (Baruk): assinatura comercial não se aplica. Abaixo, configure apenas a experiência multi-produto.';

function maskCpfCnpj(value: string) {
  return formatCpfCnpjInput(value);
}

export const CustomerEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<CustomerForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [activeTab, setActiveTab] = useState<EditTab>('dados');
  const [licenseReloadKey, setLicenseReloadKey] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const mode = form.tenantAccessMode;
  const isBarukSubdomain =
    mode === TenantAccessMode.Fit || mode === TenantAccessMode.EnterpriseBasic;

  const previewHost = useMemo(() => {
    if (!isBarukSubdomain) return '';
    const n = normalizeSlugInput(form.slug);
    return buildBarukFqdn(mode, n);
  }, [form.slug, mode, isBarukSubdomain]);

  const derivedEnterpriseSlugBase = useMemo(
    () => deriveEnterpriseCustomSlugBaseFromDomain(form.domain),
    [form.domain]
  );

  useEffect(() => {
    async function fetchCustomer() {
      setLoading(true);
      try {
        const response = await api.get(`/customers/${id}`);
        const data = response.data;
        const loadedMode = data.tenantAccessMode;
        setForm({
          ...initialForm,
          ...data,
          tenantAccessMode: isTenantAccessMode(loadedMode) ? loadedMode : '',
        });
      } catch {
        addToast('Erro ao carregar dados do cliente.', 'error');
      } finally {
        setLoading(false);
      }
    }
    fetchCustomer();
  }, [id, addToast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let newValue: string | number | boolean = value;
    if (name === 'phone') {
      newValue = maskPhone(value);
    } else if (name === 'cnpjCpf') {
      newValue = maskCpfCnpj(value);
    }
    setForm({ ...form, [name]: newValue });
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (form.isActive === undefined) newErrors.active = 'Campo Ativo é obrigatório';
    if (!form.name?.trim()) newErrors.name = 'Nome é obrigatório';
    if (!isTenantAccessMode(form.tenantAccessMode)) {
      newErrors.tenantAccessMode = 'Selecione o modo de acesso.';
    }

    if (isBarukSubdomain) {
      const n = normalizeSlugInput(form.slug);
      const slugErr = validateTenantSlugDnsLabel(n);
      if (slugErr) newErrors.slug = slugErr;
    } else {
      const domainErr = validateCustomDomain(form.domain);
      if (domainErr) newErrors.domain = domainErr;
    }

    if (!form.legalName?.trim()) newErrors.legalName = 'Razão Social é obrigatória';
    if (!form.cnpjCpf?.trim()) newErrors.cnpjCpf = 'CNPJ/CPF é obrigatório';
    else if (!validateCpfCnpj(form.cnpjCpf)) newErrors.cnpjCpf = 'CNPJ/CPF inválido';
    if (!form.email?.trim()) newErrors.email = 'E-mail é obrigatório';
    if (!form.phone?.trim()) newErrors.phone = 'Telefone é obrigatório';
    if (!form.language?.trim()) newErrors.language = 'Idioma é obrigatório';
    if (!form.timezone?.trim()) newErrors.timezone = 'Fuso horário é obrigatório';
    if (!form.dateFormat?.trim()) newErrors.dateFormat = 'Formato de data é obrigatório';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      await api.put(`/customers/${id}`, form);
      addToast('Cliente atualizado com sucesso!', 'success');
      navigate('/admin/customers');
    } catch (error: unknown) {
      let msg = 'Erro ao salvar alterações.';
      if (error && typeof error === 'object' && 'response' in error) {
        const ax = error as { response?: { data?: { message?: string; title?: string } } };
        const d = ax.response?.data;
        if (d?.message) msg = d.message;
        else if (d?.title) msg = d.title;
      }
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Carregando...</div>;
  }

  return (
    <div className="pt-8 pb-8 px-5 w-full" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h1 className="text-2xl font-bold mb-6">Editar Cliente</h1>

      <div className="border-b mb-6">
        <nav className="flex gap-1">
          <button
            type="button"
            className={`px-6 py-3 font-medium ${
              activeTab === 'dados'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('dados')}
          >
            Dados
          </button>
          <button
            type="button"
            className={`px-6 py-3 font-medium inline-flex items-center gap-1 ${
              activeTab === 'licenciamento'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            } ${form.isOwner ? 'cursor-help' : ''}`}
            title={form.isOwner ? OWNER_LICENSING_TAB_TOOLTIP : undefined}
            onClick={() => setActiveTab('licenciamento')}
          >
            Licenciamento
            {form.isOwner && (
              <span
                className="text-xs text-gray-400 font-normal"
                title={OWNER_LICENSING_TAB_TOOLTIP}
                aria-label={OWNER_LICENSING_TAB_TOOLTIP}
              >
              </span>
            )}
          </button>
          <button
            type="button"
            className={`px-6 py-3 font-medium ${
              activeTab === 'credenciais'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('credenciais')}
          >
            Credenciais de API
          </button>
        </nav>
      </div>

      {activeTab === 'credenciais' && id && (
        <CustomerApiCredentialsPanel customerId={id} />
      )}

      {activeTab === 'licenciamento' && id && (
        <>
          <CustomerSubscriptionPanel
            customerId={id}
            customerIsOwner={form.isOwner}
            onSubscriptionChanged={() => setLicenseReloadKey((key) => key + 1)}
          />
          <CustomerExperiencePanel customerId={id} reloadKey={licenseReloadKey} />
          <CustomerAgentPlatformPanel customerId={id} customerIsOwner={form.isOwner} />
        </>
      )}

      {activeTab === 'dados' && (
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-1 md:col-span-2 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <label className="block font-medium mb-1">Nome *</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={255}
              required
            />
            {errors.name && <span className="text-red-600 text-sm">{errors.name}</span>}
          </div>
          <div className="flex items-center gap-6 mt-2 md:mt-6 flex-wrap">
            <div className="flex items-center">
              <label className="block font-medium mb-1 mr-2" htmlFor="active">
                Ativo *
              </label>
              <input
                id="active"
                name="isActive"
                type="checkbox"
                checked={!!form.isActive}
                onChange={e => setForm({ ...form, isActive: e.target.checked })}
                className="h-5 w-5 cursor-pointer"
              />
            </div>
            <div className="flex items-center">
              <label className="block font-medium mb-1 mr-2" htmlFor="enableEmailNotifications">
                Habilitar notificações por e-mail
              </label>
              <input
                id="enableEmailNotifications"
                name="enableEmailNotifications"
                type="checkbox"
                checked={!!form.enableEmailNotifications}
                onChange={e => setForm({ ...form, enableEmailNotifications: e.target.checked })}
                className="h-5 w-5 cursor-pointer"
              />
            </div>
            <div className="flex items-center">
              <label className="block font-medium mb-1 mr-2" htmlFor="suggestWorkflowByDefault">
                Sugerir preenchimento de workflow por padrão
              </label>
              <input
                id="suggestWorkflowByDefault"
                name="suggestWorkflowByDefault"
                type="checkbox"
                checked={!!form.suggestWorkflowByDefault}
                onChange={e => setForm({ ...form, suggestWorkflowByDefault: e.target.checked })}
                className="h-5 w-5 cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div className="col-span-1 md:col-span-2">
          <label className="block font-medium mb-1">Modo de acesso (tenant) *</label>
          <select
            name="tenantAccessMode"
            value={form.tenantAccessMode}
            onChange={handleInputChange}
            className="w-full border rounded px-3 py-2 max-w-xl"
            required
          >
            {!isTenantAccessMode(form.tenantAccessMode) && (
              <option value="">Selecione o modo de acesso</option>
            )}
            <option value={TenantAccessMode.Fit}>
              FIT
            </option>
            <option value={TenantAccessMode.EnterpriseBasic}>
              Enterprise - Basic
            </option>
            <option value={TenantAccessMode.EnterpriseCustom}>Enterprise - Custom Domain</option>
          </select>
          {!isTenantAccessMode(form.tenantAccessMode) ? (
            <span className="text-red-600 text-sm">
              O modo de acesso gravado não é reconhecido. Escolha FIT, Enterprise Basic ou Enterprise Custom antes de salvar.
            </span>
          ) : (
            <p className="text-gray-500 text-xs mt-1">
              {isBarukSubdomain
                ? 'Informe o slug usado no subdomínio Baruk. O host completo é derivado automaticamente.'
                : 'Informe o FQDN do cliente (ex.: cliente.com.br), sem https://.'}
            </p>
          )}
        </div>

        {!isBarukSubdomain && (
          <div className="col-span-1 md:col-span-2">
            <label className="block font-medium mb-1">Domínio *</label>
            <input
              type="text"
              name="domain"
              value={form.domain}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={255}
              required
              autoComplete="off"
            />
            {errors.domain && <span className="text-red-600 text-sm">{errors.domain}</span>}
            <p className="text-gray-500 text-xs mt-1">
              FQDN em que o tenant será resolvido (sem https://). O slug interno é derivado deste domínio.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 col-span-1 md:col-span-2">
          <div>
            <label className="block font-medium mb-1">
              {isBarukSubdomain ? 'Slug *' : 'Slug (derivado do domínio)'}
            </label>
            {isBarukSubdomain ? (
              <input
                type="text"
                name="slug"
                value={form.slug}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                maxLength={100}
                required
                autoComplete="off"
              />
            ) : (
              <>
                <input
                  type="text"
                  readOnly
                  value={derivedEnterpriseSlugBase}
                  className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700"
                  aria-readonly="true"
                />
                <span className="text-gray-500 text-xs block mt-1">
                  Base do slug; ao salvar, o servidor pode acrescentar -1, -2… se houver colisão com outro cliente.
                </span>
              </>
            )}
            {errors.slug && <span className="text-red-600 text-sm">{errors.slug}</span>}
          </div>
          <div>
            <label className="block font-medium mb-1">CNPJ/CPF *</label>
            <input
              type="text"
              name="cnpjCpf"
              value={form.cnpjCpf || ''}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={20}
              required
              autoComplete="off"
            />
            {errors.cnpjCpf && <span className="text-red-600 text-sm">{errors.cnpjCpf}</span>}
          </div>
          <div>
            <label className="block font-medium mb-1">Telefone *</label>
            <input
              type="text"
              name="phone"
              value={form.phone || ''}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={20}
              required
            />
            {errors.phone && <span className="text-red-600 text-sm">{errors.phone}</span>}
          </div>
        </div>

        <div>
          <label className="block font-medium mb-1">E-mail *</label>
          <input
            type="email"
            name="email"
            value={form.email || ''}
            onChange={handleInputChange}
            className="w-full border rounded px-3 py-2"
            maxLength={255}
            required
            autoComplete="off"
          />
          {errors.email && <span className="text-red-600 text-sm">{errors.email}</span>}
        </div>

        {isBarukSubdomain ? (
          <div>
            <label className="block font-medium mb-1">Host derivado (somente leitura)</label>
            <input
              type="text"
              readOnly
              value={previewHost}
              className="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700"
            />
            <span className="text-gray-500 text-xs block mt-1">
              Deve coincidir com o cadastro e com a resolução por host na API.
            </span>
          </div>
        ) : (
          <div>
            <label className="block font-medium mb-1">Razão Social *</label>
            <input
              type="text"
              name="legalName"
              value={form.legalName || ''}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={255}
              required
            />
            {errors.legalName && <span className="text-red-600 text-sm">{errors.legalName}</span>}
          </div>
        )}

        {isBarukSubdomain ? (
          <>
            <div>
              <label className="block font-medium mb-1">Razão Social *</label>
              <input
                type="text"
                name="legalName"
                value={form.legalName || ''}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                maxLength={255}
                required
              />
              {errors.legalName && <span className="text-red-600 text-sm">{errors.legalName}</span>}
            </div>
            <div>
              <label className="block font-medium mb-1">Título do App</label>
              <input
                type="text"
                name="appTitle"
                value={form.appTitle || ''}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
                maxLength={255}
              />
            </div>
          </>
        ) : (
          <div className="col-span-1 md:col-span-2">
            <label className="block font-medium mb-1">Título do App</label>
            <input
              type="text"
              name="appTitle"
              value={form.appTitle || ''}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={255}
            />
          </div>
        )}
        <div>
          <label className="block font-medium mb-1">Logo URL</label>
          <input
            type="text"
            name="logoUrl"
            value={form.logoUrl || ''}
            onChange={handleInputChange}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Favicon URL</label>
          <input
            type="text"
            name="faviconUrl"
            value={form.faviconUrl || ''}
            onChange={handleInputChange}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Cor Primária</label>
          <input
            type="color"
            name="primaryColor"
            value={form.primaryColor && /^#[0-9A-Fa-f]{6}$/.test(form.primaryColor) ? form.primaryColor : '#000000'}
            onChange={handleInputChange}
            className="w-16 h-10 p-0 border rounded cursor-pointer"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Cor Secundária</label>
          <input
            type="color"
            name="secondaryColor"
            value={form.secondaryColor && /^#[0-9A-Fa-f]{6}$/.test(form.secondaryColor) ? form.secondaryColor : '#ffffff'}
            onChange={handleInputChange}
            className="w-16 h-10 p-0 border rounded cursor-pointer"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 col-span-1 md:col-span-2">
          <div>
            <label className="block font-medium mb-1">Idioma *</label>
            <select
              name="language"
              value={form.language || 'pt-BR'}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="pt-BR">Português (Brasil)</option>
            </select>
            {errors.language && <span className="text-red-600 text-sm">{errors.language}</span>}
          </div>
          <div>
            <label className="block font-medium mb-1">Formato de Data *</label>
            <input
              type="text"
              name="dateFormat"
              value={form.dateFormat || ''}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              maxLength={20}
              required
            />
            {errors.dateFormat && <span className="text-red-600 text-sm">{errors.dateFormat}</span>}
          </div>
          <div>
            <label className="block font-medium mb-1">Fuso Horário *</label>
            <select
              name="timezone"
              value={form.timezone || 'America/Sao_Paulo'}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="America/Sao_Paulo">America/Sao_Paulo</option>
              <option value="America/Manaus">America/Manaus</option>
              <option value="America/Fortaleza">America/Fortaleza</option>
              <option value="America/Cuiaba">America/Cuiaba</option>
              <option value="America/Recife">America/Recife</option>
            </select>
            {errors.timezone && <span className="text-red-600 text-sm">{errors.timezone}</span>}
          </div>
        </div>
        <div className="flex items-end h-full mt-6 md:mt-0 justify-end gap-2 col-span-1 md:col-span-2">
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/customers')}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            Salvar
          </Button>
        </div>
      </form>
      )}
    </div>
  );
};
